import { createStyles, Group, Text } from '@mantine/core';
import { find, first, reduce, size, takeRight } from 'lodash/fp';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { SpaceType, useSpaces } from '@portals/api/organizations';

import { SpaceNameInput } from './SpaceNameInput';
import { useOverviewContext } from '../overview.context';

const getSpacePath = (selectedSpace: SpaceType, spaces: SpaceType[]) => {
  return reduce(
    (acc: Array<string>, curr: number) => {
      const spaceName = find({ id: curr }, spaces)?.name;

      if (!spaceName) return acc;

      acc.push(spaceName);

      return acc;
    },
    [],
    selectedSpace?.path
  );
};

type SpacePathProps = {
  space: SpaceType;
};

export function SpacePath({ space }: SpacePathProps) {
  const spaces = useSpaces();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const overview = useOverviewContext();

  const path = useMemo(() => {
    const pathNames = getSpacePath(space, spaces.data);
    const numOfSpaces = size(pathNames);

    if (numOfSpaces > 4) {
      const firstPathName = first(pathNames);
      const lastTwoPathNames = takeRight(2, pathNames);

      return (
        <>
          <Text
            className="path path-link"
            title={firstPathName}
            onClick={() => navigate(`/overview/${space.path[0]}`)}
          >
            {`${firstPathName}`}
          </Text>

          <Text className="path">/</Text>

          <Text>...</Text>

          <Text className="path">/</Text>

          <Text
            className="path path-link"
            title={lastTwoPathNames[0]}
            onClick={() => navigate(`/overview/${space.path[numOfSpaces - 2]}`)}
          >
            {`${lastTwoPathNames[0]}`}
          </Text>

          <Text className="path">/</Text>

          {overview.spaceEditModeId === space.id ? (
            <SpaceNameInput key={space.id} />
          ) : (
            <Text title={lastTwoPathNames[1]} className="path active">
              {lastTwoPathNames[1]}
            </Text>
          )}
        </>
      );
    }

    return pathNames.map((spaceName, index) => {
      const isSelected = index === numOfSpaces - 1;

      if (isSelected) {
        return overview.spaceEditModeId === space.id ? (
          <SpaceNameInput key={space.id} />
        ) : (
          <Text key={index} title={spaceName} className="path active">
            {spaceName}
          </Text>
        );
      }

      return (
        <React.Fragment key={index}>
          <Text
            className="path path-link"
            title={spaceName}
            onClick={() => navigate(`/overview/${space.path[index]}`)}
          >
            {`${spaceName}`}
          </Text>

          <Text className="path-link">/</Text>
        </React.Fragment>
      );
    });
  }, [space, spaces.data, navigate, overview.spaceEditModeId]);

  return (
    <Group spacing={10} className={classes.container} noWrap>
      {path}
    </Group>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    '.path ': {
      fontSize: theme.fontSizes.xl,
      color: theme.colors.blue_gray[6],

      '&.active': {
        color: theme.colors.blue_gray[9],
        fontWeight: 600,
      },

      '&.path-link': {
        '&:not(.active)': {
          cursor: 'pointer',
        },
      },
    },
  },
}));
